import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { apiFetch } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = {
  positive: '#2eb4ac',
  neutral: '#94a3b8',
  negative: '#ef4444',
};

export default function SentimentPage() {
  const [trendData, setTrendData] = useState([]);
  const [platformData, setPlatformData] = useState([]);
  const [brandData, setBrandData] = useState([]);
  const [ratingData, setRatingData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [trendRes, platformRes, brandRes, ratingRes] = await Promise.all([
        apiFetch('/api/analytics/sentiment-trend?days=30', { credentials: 'include' }),
        apiFetch('/api/analytics/sentiment-by-platform', { credentials: 'include' }),
        apiFetch('/api/analytics/sentiment-by-brand', { credentials: 'include' }),
        apiFetch('/api/analytics/rating-distribution', { credentials: 'include' }),
      ]);

      if (trendRes.ok) setTrendData(await trendRes.json());
      if (platformRes.ok) setPlatformData(await platformRes.json());
      if (brandRes.ok) setBrandData(await brandRes.json());
      if (ratingRes.ok) setRatingData(await ratingRes.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-2">{label}</p>
          {payload.map((entry) => (
            <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[280px] w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sentiment-page">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Sentiment Analytics</h1>
        <p className="text-muted-foreground mt-1">Deep insights into customer perception trends</p>
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sentiment Trend Over Time */}
        <Card className="lg:col-span-2 card-hover" data-testid="sentiment-trend-chart">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#2eb4ac]" />
              Sentiment Trend Over Time
            </CardTitle>
            <CardDescription>Last 30 days sentiment distribution</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[320px]">
              {trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="date"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="positive"
                      name="Positive"
                      stroke={COLORS.positive}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="neutral"
                      name="Neutral"
                      stroke={COLORS.neutral}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="negative"
                      name="Negative"
                      stroke={COLORS.negative}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No trend data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sentiment by Platform */}
        <Card className="card-hover" data-testid="sentiment-by-platform-chart">
          <CardHeader>
            <CardTitle className="font-heading">Sentiment by Platform</CardTitle>
            <CardDescription>Comparison across marketplaces</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {platformData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={platformData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis
                      type="category"
                      dataKey="platform"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      width={100}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="positive" name="Positive" fill={COLORS.positive} stackId="a" />
                    <Bar dataKey="neutral" name="Neutral" fill={COLORS.neutral} stackId="a" />
                    <Bar dataKey="negative" name="Negative" fill={COLORS.negative} stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No platform data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Sentiment by Brand */}
        <Card className="card-hover" data-testid="sentiment-by-brand-chart">
          <CardHeader>
            <CardTitle className="font-heading">Sentiment by Brand</CardTitle>
            <CardDescription>Performance across brands</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {brandData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={brandData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis
                      type="category"
                      dataKey="brand"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      width={80}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="positive" name="Positive" fill={COLORS.positive} stackId="a" />
                    <Bar dataKey="neutral" name="Neutral" fill={COLORS.neutral} stackId="a" />
                    <Bar dataKey="negative" name="Negative" fill={COLORS.negative} stackId="a" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No brand data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Rating Distribution */}
        <Card className="lg:col-span-2 card-hover" data-testid="rating-distribution-chart">
          <CardHeader>
            <CardTitle className="font-heading">Rating Distribution</CardTitle>
            <CardDescription>Breakdown of star ratings (1-5)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              {ratingData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ratingData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="rating"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickFormatter={(value) => `${value} ★`}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="count"
                      name="Reviews"
                      fill="#2eb4ac"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  No rating data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
